import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Filter, Calendar as CalendarIcon, Inbox, CheckCircle2, XCircle, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const STATUS_LABELS = {
  pending: { label: "申請中", className: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  approved: { label: "承認済", className: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  rejected: { label: "却下", className: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  partially_approved: { label: "一部承認", className: "bg-blue-100 text-blue-700 border-blue-200", icon: CheckCircle2 },
};

const ITEM_STATUS_LABELS = {
  pending: { label: "未処理", className: "bg-slate-100 text-slate-600 border-slate-200" },
  approved: { label: "承認", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  rejected: { label: "却下", className: "bg-red-100 text-red-700 border-red-200" },
};

const ACTION_LABELS = {
  modify: { label: "変更", className: "bg-amber-100 text-amber-700 border-amber-200" },
  add: { label: "追加", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  new_line: { label: "締切後発注", className: "bg-sky-100 text-sky-700 border-sky-200" },
  delete: { label: "削除", className: "bg-red-100 text-red-700 border-red-200" },
};

export default function BatchChangeRequestsView({ title, description, scope }) {
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [batchesRes, productsRes, storesRes] = await Promise.all([
        axios.get(`${API}/orders/change-requests/batch`, getAuthHeaders()),
        axios.get(`${API}/products`, getAuthHeaders()),
        axios.get(`${API}/stores`, getAuthHeaders()).catch(() => ({ data: [] })),
      ]);
      setBatches(Array.isArray(batchesRes.data) ? batchesRes.data : []);
      setProducts(productsRes.data);
      setStores(storesRes.data);
    } catch (e) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const productsMap = useMemo(() => {
    const m = {};
    products.forEach((p) => (m[p.id] = p));
    return m;
  }, [products]);

  const storesMap = useMemo(() => {
    const m = {};
    stores.forEach((s) => (m[s.id] = s));
    return m;
  }, [stores]);

  const filtered = useMemo(() => {
    if (filter === "all") return batches;
    return batches.filter((b) => b.status === filter);
  }, [batches, filter]);

  const counts = useMemo(() => {
    const c = { all: batches.length, pending: 0, approved: 0, rejected: 0, partially_approved: 0 };
    batches.forEach((b) => {
      if (c[b.status] !== undefined) c[b.status] += 1;
    });
    return c;
  }, [batches]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold font-chivo tracking-tight text-slate-800">
            {title}
          </h2>
          <p className="text-sm text-slate-600 mt-1">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden">
            {[
              { id: "all", label: "すべて" },
              { id: "pending", label: "申請中" },
              { id: "approved", label: "承認済" },
              { id: "partially_approved", label: "一部承認" },
              { id: "rejected", label: "却下" },
            ].map((opt) => (
              <button
                key={opt.id}
                data-testid={`history-filter-${opt.id}`}
                onClick={() => setFilter(opt.id)}
                className={`h-9 px-3 text-xs ${
                  filter === opt.id
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {opt.label}
                <span className="ml-1 text-[10px] opacity-80">({counts[opt.id] ?? 0})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">読み込み中...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500 border border-slate-200 rounded-sm bg-white">
          <Inbox className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          該当する変更申請はありません
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((b) => {
            const sLabel = STATUS_LABELS[b.status] || STATUS_LABELS.pending;
            const StatusIcon = sLabel.icon;
            const counts = {
              modify: (b.items || []).filter((i) => i.action === "modify").length,
              add: (b.items || []).filter((i) => i.action === "add").length,
              delete: (b.items || []).filter((i) => i.action === "delete").length,
            };
            return (
              <div
                key={b.id}
                data-testid={`history-batch-${b.id}`}
                className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden"
              >
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap text-sm">
                    {scope !== "store" && (
                      <span className="font-semibold text-slate-700">
                        {storesMap[b.store_id]?.name || "—"}
                        {storesMap[b.store_id]?.code ? ` (${storesMap[b.store_id].code})` : ""}
                      </span>
                    )}
                    <CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-slate-500">配送日:</span>
                    <span className="font-mono text-slate-700">{b.delivery_date}</span>
                    <span className="text-slate-500">申請日時:</span>
                    <span className="text-slate-600 text-xs">
                      {new Date(b.created_at).toLocaleString("ja-JP")}
                    </span>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm border font-medium ${sLabel.className}`}>
                    <StatusIcon className="h-3 w-3" />
                    {sLabel.label}
                  </span>
                </div>
                <div className="px-4 py-2 bg-slate-50/50 border-b border-slate-100 text-xs text-slate-600">
                  変更{counts.modify} / 追加{counts.add} / 削除{counts.delete}
                  {b.review_comment && (
                    <span className="ml-3 text-slate-700">
                      コメント: <span className="italic">{b.review_comment}</span>
                    </span>
                  )}
                </div>
                <table className="w-full">
                  <thead className="bg-slate-50/50 border-b border-slate-100">
                    <tr>
                      <th className="h-8 px-4 text-left text-[10px] font-medium text-slate-500 uppercase w-20">種別</th>
                      <th className="h-8 px-4 text-left text-[10px] font-medium text-slate-500 uppercase">商品</th>
                      <th className="h-8 px-4 text-right text-[10px] font-medium text-slate-500 uppercase">変更内容</th>
                      <th className="h-8 px-4 text-center text-[10px] font-medium text-slate-500 uppercase w-24">処理</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(b.items || []).map((it, i) => {
                      const p = productsMap[it.product_id];
                      const a = it.action === "add" && it.is_new_line ? ACTION_LABELS.new_line : ACTION_LABELS[it.action] || ACTION_LABELS.modify;
                      const itStat = ITEM_STATUS_LABELS[it.status || "pending"] || ITEM_STATUS_LABELS.pending;
                      return (
                        <tr
                          key={i}
                          className="border-b border-slate-100 last:border-b-0"
                          data-testid={`history-item-${b.id}-${i}`}
                        >
                          <td className="p-2 px-4">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${a.className}`}>
                              {a.label}
                            </span>
                          </td>
                          <td className="p-2 px-4 text-sm">
                            <div className="font-medium">{p?.name || "—"}</div>
                            <div className="text-xs text-slate-500">{p?.code}</div>
                          </td>
                          <td className="p-2 px-4 text-sm font-mono text-right">
                            {it.action === "modify"
                              ? `${(it.old_quantity || 0).toFixed(2)} ${it.old_unit || ""} → ${(it.new_quantity || 0).toFixed(2)} ${it.new_unit || ""}`
                              : it.action === "add"
                              ? `+ ${(it.new_quantity || 0).toFixed(2)} ${it.new_unit || ""}`
                              : `- ${(it.old_quantity || 0).toFixed(2)} ${it.old_unit || ""}`}
                          </td>
                          <td className="p-2 px-4 text-center">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${itStat.className}`}>
                              {itStat.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
